import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { removeUndefined } from '../../util/general';
import { restoreAll } from '../restoreAll';
import { ProjectWithClientInfo, ProjectWithAssigneesDto, ProjectTotalEarningsDto, ProjectOverviewDto } from '../../commonTypes/acs';
import { ProjectToUpdate } from '../../hooks/project/projectApi/useHttpPatchProject';
import { ProjectDtoRelV2, ActivityFeedWithUserDto, ProjectRoleDto } from '../../commonTypes/project.types';
import { ProjectHoursSummaryWithPercentageDto, UserTimeLogRateBreakdownDto } from '../../commonTypes/timelog.types';
import { ActivityLogWithUsers } from '../../commonTypes/ActivityLogTypes/activityLog.types';
import { SelectItem } from '../../components/Common/SearchableMultiSelectDropdown';

export interface UserTimeLogsCache {
    totalSubmitted: number;
    totalUnsubmitted: number;
    currentMonthHours: number;
    totalLoggedHours: number;
    rateBreakdown?: UserTimeLogRateBreakdownDto[];
}

export interface ProjectsState {
    value: ProjectWithClientInfo[];
    showCreateProjectForm: boolean;
    showEditProjectForm: boolean;
    cachedProjects: ProjectDtoRelV2[];
    lastFetched: number | null;
    isLoading: boolean;
    cachedSingleProjects: Record<string, ProjectWithAssigneesDto>;
    singleProjectsLastFetched: Record<string, number>;
    cachedHoursSummary: Record<string, ProjectHoursSummaryWithPercentageDto>;
    hoursSummaryLastFetched: Record<string, number>;
    cachedEarnings: Record<string, ProjectTotalEarningsDto>;
    earningsLastFetched: Record<string, number>;
    cachedProjectSummaryOverview: Record<string, ProjectOverviewDto[]>;
    projectSummaryOverviewLastFetched: Record<string, number>;
    cachedUserTimeLogs: Record<string, UserTimeLogsCache>;
    userTimeLogsLastFetched: Record<string, number>;
    cachedActivityLogs: Record<string, ActivityLogWithUsers[]>;
    activityLogsLastFetched: Record<string, number>;
    cachedActivityFeed: Record<string, ActivityFeedWithUserDto>;
    activityFeedLastFetched: Record<string, number>;
    cachedActivityFeedByClient: Record<string, ActivityFeedWithUserDto>;
    activityFeedByClientLastFetched: Record<string, number>;
    cachedProjectRoles: ProjectRoleDto[];
    projectRolesLastFetched: number | null;
    cachedPossibleAssignees: Record<string, SelectItem[]>;
    possibleAssigneesLastFetched: Record<string, number>;
}

const initialState: ProjectsState = {
    value: [],
    showCreateProjectForm: false,
    showEditProjectForm: false,
    cachedProjects: [],
    lastFetched: null,
    isLoading: false,
    cachedSingleProjects: {},
    singleProjectsLastFetched: {},
    cachedHoursSummary: {},
    hoursSummaryLastFetched: {},
    cachedEarnings: {},
    earningsLastFetched: {},
    cachedProjectSummaryOverview: {},
    projectSummaryOverviewLastFetched: {},
    cachedUserTimeLogs: {},
    userTimeLogsLastFetched: {},
    cachedActivityLogs: {},
    activityLogsLastFetched: {},
    cachedActivityFeed: {},
    activityFeedLastFetched: {},
    cachedActivityFeedByClient: {},
    activityFeedByClientLastFetched: {},
    cachedProjectRoles: [],
    projectRolesLastFetched: null,
    cachedPossibleAssignees: {},
    possibleAssigneesLastFetched: {},
};

const projectsSlice = createSlice({
    name: 'projects',
    initialState,
    extraReducers: builder => builder.addCase(restoreAll, () => initialState),
    reducers: {
        setProjects: (state, action: PayloadAction<ProjectWithClientInfo[]>) => {
            state.value = action.payload;
        },
        addProject: (state, action: PayloadAction<ProjectWithClientInfo>) => {
            state.value.push({ ...action.payload });
        },
        setShowCreateProjectForm: (state, action: PayloadAction<boolean>) => {
            state.showCreateProjectForm = action.payload;
        },
        setShowEditProjectForm: (state, action: PayloadAction<boolean>) => {
            state.showEditProjectForm = action.payload;
        },
        editProject: (state, action: PayloadAction<ProjectToUpdate>) => {
            const { projectId, updateProject } = action.payload;
            const projectToUpdate = state.value.find(client => projectId === client.id);
            if (!projectToUpdate) {
                console.log('Project to be updated does not exits');
                return;
            }

            const filteredPayload = removeUndefined(updateProject);
            Object.assign(projectToUpdate, filteredPayload);
        },
        deleteProject: (state, action: PayloadAction<string>) => {
            state.value = state.value.filter(project => project.id !== action.payload);
        },
        removePending: (state, action: PayloadAction<ProjectWithClientInfo>) => {
            let found = false;

            state.value = state.value.map(project => {
                if (project.id !== action.payload.id) return project;
                found = true;
                return { ...project, pending: false };
            });

            if (found) return;

            state.value.push(action.payload);
        },
        setProjectsLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setCachedProjects: (state, action: PayloadAction<ProjectDtoRelV2[]>) => {
            state.cachedProjects = action.payload;
            state.lastFetched = Date.now();
            state.isLoading = false;
        },
        updateCachedProject: (state, action: PayloadAction<ProjectDtoRelV2>) => {
            const index = state.cachedProjects.findIndex(p => p.id === action.payload.id);
            if (index !== -1) {
                state.cachedProjects[index] = action.payload;
            }
        },
        invalidateProjectsCache: state => {
            state.lastFetched = null;
            state.cachedProjects = [];
        },
        setCachedSingleProject: (state, action: PayloadAction<ProjectWithAssigneesDto>) => {
            const project = action.payload;
            state.cachedSingleProjects[project.id] = project;
            state.singleProjectsLastFetched[project.id] = Date.now();
        },
        updateCachedSingleProject: (state, action: PayloadAction<Partial<ProjectWithAssigneesDto> & { id: string }>) => {
            const { id, ...updates } = action.payload;
            if (state.cachedSingleProjects[id]) {
                state.cachedSingleProjects[id] = { ...state.cachedSingleProjects[id], ...updates };
            }
        },
        invalidateSingleProjectCache: (state, action: PayloadAction<string>) => {
            delete state.cachedSingleProjects[action.payload];
            delete state.singleProjectsLastFetched[action.payload];
        },
        invalidateAllSingleProjectsCache: state => {
            state.cachedSingleProjects = {};
            state.singleProjectsLastFetched = {};
        },
        setCachedHoursSummary: (state, action: PayloadAction<{ projectId: string; data: ProjectHoursSummaryWithPercentageDto }>) => {
            state.cachedHoursSummary[action.payload.projectId] = action.payload.data;
            state.hoursSummaryLastFetched[action.payload.projectId] = Date.now();
        },
        setCachedEarnings: (state, action: PayloadAction<{ projectId: string; data: ProjectTotalEarningsDto }>) => {
            state.cachedEarnings[action.payload.projectId] = action.payload.data;
            state.earningsLastFetched[action.payload.projectId] = Date.now();
        },
        invalidateProjectMetrics: (state, action: PayloadAction<string>) => {
            delete state.cachedHoursSummary[action.payload];
            delete state.hoursSummaryLastFetched[action.payload];
            delete state.cachedEarnings[action.payload];
            delete state.earningsLastFetched[action.payload];
        },
        setCachedProjectSummaryOverview: (state, action: PayloadAction<{ key: string; data: ProjectOverviewDto[] }>) => {
            state.cachedProjectSummaryOverview[action.payload.key] = action.payload.data;
            state.projectSummaryOverviewLastFetched[action.payload.key] = Date.now();
        },
        setCachedUserTimeLogs: (state, action: PayloadAction<{ key: string; data: UserTimeLogsCache }>) => {
            state.cachedUserTimeLogs[action.payload.key] = action.payload.data;
            state.userTimeLogsLastFetched[action.payload.key] = Date.now();
        },
        setCachedActivityLogs: (state, action: PayloadAction<{ projectId: string; data: ActivityLogWithUsers[] }>) => {
            state.cachedActivityLogs[action.payload.projectId] = action.payload.data;
            state.activityLogsLastFetched[action.payload.projectId] = Date.now();
        },
        invalidateTimesheetCache: (state, action: PayloadAction<string>) => {
            Object.keys(state.cachedProjectSummaryOverview).forEach(key => {
                if (key.startsWith(`${action.payload}-`)) {
                    delete state.cachedProjectSummaryOverview[key];
                    delete state.projectSummaryOverviewLastFetched[key];
                }
            });
            Object.keys(state.cachedUserTimeLogs).forEach(key => {
                if (key.startsWith(`${action.payload}-`)) {
                    delete state.cachedUserTimeLogs[key];
                    delete state.userTimeLogsLastFetched[key];
                }
            });
        },
        invalidateActivityLogsCache: (state, action: PayloadAction<string>) => {
            delete state.cachedActivityLogs[action.payload];
            delete state.activityLogsLastFetched[action.payload];
        },
        setCachedActivityFeed: (state, action: PayloadAction<{ projectId: string; data: ActivityFeedWithUserDto }>) => {
            state.cachedActivityFeed[action.payload.projectId] = action.payload.data;
            state.activityFeedLastFetched[action.payload.projectId] = Date.now();
        },
        invalidateActivityFeedCache: (state, action: PayloadAction<string>) => {
            delete state.cachedActivityFeed[action.payload];
            delete state.activityFeedLastFetched[action.payload];
        },
        setCachedActivityFeedByClient: (state, action: PayloadAction<{ clientId: string; data: ActivityFeedWithUserDto }>) => {
            state.cachedActivityFeedByClient[action.payload.clientId] = action.payload.data;
            state.activityFeedByClientLastFetched[action.payload.clientId] = Date.now();
        },
        invalidateActivityFeedByClientCache: (state, action: PayloadAction<string>) => {
            delete state.cachedActivityFeedByClient[action.payload];
            delete state.activityFeedByClientLastFetched[action.payload];
        },
        setCachedProjectRoles: (state, action: PayloadAction<ProjectRoleDto[]>) => {
            state.cachedProjectRoles = action.payload;
            state.projectRolesLastFetched = Date.now();
        },
        invalidateProjectRolesCache: state => {
            state.cachedProjectRoles = [];
            state.projectRolesLastFetched = null;
        },
        setCachedPossibleAssignees: (state, action: PayloadAction<{ key: string; data: SelectItem[] }>) => {
            state.cachedPossibleAssignees[action.payload.key] = action.payload.data;
            state.possibleAssigneesLastFetched[action.payload.key] = Date.now();
        },
        invalidatePossibleAssigneesCache: (state, action: PayloadAction<string>) => {
            delete state.cachedPossibleAssignees[action.payload];
            delete state.possibleAssigneesLastFetched[action.payload];
        },
    },
});

export const {
    setProjects,
    addProject,
    editProject,
    setProjectsLoading,
    setCachedProjects,
    updateCachedProject,
    invalidateProjectsCache,
    setCachedSingleProject,
    updateCachedSingleProject,
    invalidateSingleProjectCache,
    invalidateAllSingleProjectsCache,
    setCachedHoursSummary,
    setCachedEarnings,
    invalidateProjectMetrics,
    setCachedProjectSummaryOverview,
    setCachedUserTimeLogs,
    setCachedActivityLogs,
    invalidateTimesheetCache,
    invalidateActivityLogsCache,
    setCachedActivityFeed,
    invalidateActivityFeedCache,
    setCachedActivityFeedByClient,
    invalidateActivityFeedByClientCache,
    setCachedProjectRoles,
    invalidateProjectRolesCache,
    setCachedPossibleAssignees,
    invalidatePossibleAssigneesCache,
} = projectsSlice.actions;

export const selectCachedProjects = (state: { projects: ProjectsState }) => state.projects.cachedProjects;
export const selectProjectsLoading = (state: { projects: ProjectsState }) => state.projects.isLoading;
export const selectProjectsLastFetched = (state: { projects: ProjectsState }) => state.projects.lastFetched;

export const selectCachedSingleProject = (projectId: string) => (state: { projects: ProjectsState }) =>
    state.projects.cachedSingleProjects[projectId];
export const selectSingleProjectLastFetched = (projectId: string) => (state: { projects: ProjectsState }) =>
    state.projects.singleProjectsLastFetched[projectId];

export const selectCachedHoursSummary = (projectId: string) => (state: { projects: ProjectsState }) => state.projects.cachedHoursSummary[projectId];
export const selectHoursSummaryLastFetched = (projectId: string) => (state: { projects: ProjectsState }) =>
    state.projects.hoursSummaryLastFetched[projectId];
export const selectCachedEarnings = (projectId: string) => (state: { projects: ProjectsState }) => state.projects.cachedEarnings[projectId];
export const selectEarningsLastFetched = (projectId: string) => (state: { projects: ProjectsState }) => state.projects.earningsLastFetched[projectId];

export const selectCachedProjectSummaryOverview = (key: string) => (state: { projects: ProjectsState }) =>
    state.projects.cachedProjectSummaryOverview[key];
export const selectProjectSummaryOverviewLastFetched = (key: string) => (state: { projects: ProjectsState }) =>
    state.projects.projectSummaryOverviewLastFetched[key];
export const selectCachedUserTimeLogs = (key: string) => (state: { projects: ProjectsState }) => state.projects.cachedUserTimeLogs[key];
export const selectUserTimeLogsLastFetched = (key: string) => (state: { projects: ProjectsState }) => state.projects.userTimeLogsLastFetched[key];
export const selectCachedActivityLogs = (projectId: string) => (state: { projects: ProjectsState }) => state.projects.cachedActivityLogs[projectId];
export const selectActivityLogsLastFetched = (projectId: string) => (state: { projects: ProjectsState }) =>
    state.projects.activityLogsLastFetched[projectId];

export const selectCachedActivityFeed = (projectId: string) => (state: { projects: ProjectsState }) => state.projects.cachedActivityFeed[projectId];
export const selectActivityFeedLastFetched = (projectId: string) => (state: { projects: ProjectsState }) =>
    state.projects.activityFeedLastFetched[projectId];

export const selectCachedActivityFeedByClient = (clientId: string) => (state: { projects: ProjectsState }) =>
    state.projects.cachedActivityFeedByClient[clientId];
export const selectActivityFeedByClientLastFetched = (clientId: string) => (state: { projects: ProjectsState }) =>
    state.projects.activityFeedByClientLastFetched[clientId];

export const selectCachedProjectRoles = (state: { projects: ProjectsState }) => state.projects.cachedProjectRoles;
export const selectProjectRolesLastFetched = (state: { projects: ProjectsState }) => state.projects.projectRolesLastFetched;

export const selectCachedPossibleAssignees = (key: string) => (state: { projects: ProjectsState }) => state.projects.cachedPossibleAssignees[key];
export const selectPossibleAssigneesLastFetched = (key: string) => (state: { projects: ProjectsState }) =>
    state.projects.possibleAssigneesLastFetched[key];

export default projectsSlice.reducer;
